import {
  faPencilAlt,
  faTimes,
  faTrash,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import classNames from 'classnames';
import Button from 'components/base/Button';
import Lightbox from 'components/base/LightBox';
import NutritionTable, {
  nutritionTableColumns
} from 'components/tables/NutritionTable';
import { errorHandler } from 'helpers/response-utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useLightbox from 'hooks/useLightbox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useCalendarContext } from 'providers/CalendarProvider';
import moment from 'moment';

// FIX: added useMemo and useCallback
import { useEffect, useState, useMemo, useCallback } from 'react';

import { Col, Modal, Row, Toast, ToastContainer } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { REMOVE_EVENT, SET_CALENDAR_STATE } from 'reducers/CalendarReducer';
import FoodPaichart from './FoodPaichart';
import AddFoodItemModal from './AddFoodItemModal';
import { NutritionFact } from 'services/foodSearchService';

const CalendarEventModal = () => {
  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { selectedEvent, calendarDispatch } = useCalendarContext();
  const [events, setEvents] = useState(selectedEvent ? selectedEvent : null);
  const [modalName, setModalName] = useState('info-modal');
  const [showAddFoodModal, setShowAddFoodModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const { id } = useParams();
  const token = localStorage.getItem('token');

  const { lightboxProps, openLightbox } = useLightbox([
    selectedEvent?.extendedProps.mealFile
  ]);

  useEffect(() => {
    setEvents(selectedEvent);
  }, [selectedEvent]);

  const handleClose = () => {
    calendarDispatch({
      type: SET_CALENDAR_STATE,
      payload: {
        selectedEvent: null
      }
    });
  };

  // open confirmation modal
  const handleDeleteNutrient = useCallback((index: number) => {
    setDeleteIndex(index);
    setShowDeleteModal(true);
  }, []);

  const confirmDeleteNutrient = async () => {
    if (deleteIndex === null || !events || !selectedEvent) return;

    try {
      setIsDeleting(true);

      const item = events.extendedProps?.nutrient?.[deleteIndex];

      if (!item || !item.id) {
        alert('Item ID not found');
        return;
      }

      // API call
      await axios.delete(
        `${process.env.REACT_APP_ENDPOINT}/clinic/patient/${id}/meal/${selectedEvent.id}/item/${item.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // IMPORTANT: delete by ID, NOT index
      setEvents(prev => {
        if (!prev) return prev;

        const updated = prev.extendedProps.nutrient.filter(
          (n: any) => n.id !== item.id
        );

        selectedEvent.setExtendedProp('nutrient', updated);

        return {
          ...prev,
          extendedProps: {
            ...prev.extendedProps,
            nutrient: updated
          }
        } as any;
      });

      setShowDeleteModal(false);
      setDeleteIndex(null);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemove = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_ENDPOINT}/clinic/patient/${id}/meal/${selectedEvent?.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }
      calendarDispatch({
        type: REMOVE_EVENT
      });
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const handleCloseEditInfoModal = async () => {
    setModalName('info-modal');
  };

  const handleAddFoodItem = async (item: {
    foodItemId: string;
    name: string;
    name_ar?: string | null;
    brand_name_en?: string | null;
    brand_name_ar?: string | null;
    quantity: string;
    servingSize: number;
    servingSizeUnit: string;
    nutritionFact: NutritionFact[];
  }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/clinic/meal/${selectedEvent?.id}/item`,
        {
          foodItemId: item.foodItemId,
          name: item.name,
          name_ar: item.name_ar,
          brand_name_en: item.brand_name_en,
          brand_name_ar: item.brand_name_ar,
          quantity: item.servingSize.toString(),
          servingSize: item.servingSize,
          servingSizeUnit: item.servingSizeUnit,
          nutritionFact: item.nutritionFact
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === true) {
        const createdItem = response.data.data;

        setToastMessage({
          type: 'success',
          message: 'Food item added successfully!'
        });

        if (selectedEvent) {
          const currentNutrients = selectedEvent.extendedProps.nutrient || [];

          const updatedNutrients = Array.isArray(currentNutrients)
            ? [...currentNutrients]
            : JSON.parse(currentNutrients || '[]');

          // PUSH REAL DB ITEM (WITH ID)
          updatedNutrients.push(createdItem);

          selectedEvent.setExtendedProp('nutrient', updatedNutrients);

          setEvents((prevEvents: any) => ({
            ...prevEvents,
            extendedProps: {
              ...prevEvents.extendedProps,
              nutrient: updatedNutrients
            }
          }));
        }

        setModalName('edit-info-modal');
      } else {
        setToastMessage({
          type: 'error',
          message: response.data.error?.message || 'Failed to add food item'
        });
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to add food item';
      setToastMessage({
        type: 'error',
        message: errorMsg
      });
    }
  };

  const handleOpenFoodModal = () => {
    setModalName('add-food-modal');
  };

  const handleCloseFoodModal = () => {
    setModalName('edit-info-modal');
    setShowAddFoodModal(false);
  };

  const handleEdit = async () => {
    setModalName('edit-info-modal');
  };

  const updateMeal = async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/clinic/meal`,
        {
          patientID: id,
          mealID: selectedEvent?.id,
          nutrient: events?.extendedProps.nutrient
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.data.status) {
        errorHandler(response.data);
        return;
      }

      const updatedItems = response.data.data;

      // Update FullCalendar instance
      selectedEvent?.setExtendedProp('nutrient', updatedItems);

      // Update local state
      setEvents((prev: any) => ({
        ...prev,
        extendedProps: {
          ...prev.extendedProps,
          nutrient: updatedItems
        }
      }));

      handleCloseEditInfoModal();
    } catch (error) {
      console.error(error);
    }
  };

  const handleNutrientChange = async (index: number, newQuantity: any) => {
    setEvents((events: any) => {
      const updatedNutrients = events?.extendedProps.nutrient.map(
        (nutrient: any, i: number) =>
          i === index ? { ...nutrient, quantity: newQuantity } : nutrient
      );

      return {
        ...events,
        extendedProps: {
          ...events.extendedProps,
          nutrient: updatedNutrients
        }
      };
    });
  };

  // FIX: memoized data
  const nutrientData = useMemo(() => {
    return events?.extendedProps?.nutrient || [];
  }, [events]);

  // FIX: memoized columns
  const nutrientColumns = useMemo(() => {
    return nutritionTableColumns(handleDeleteNutrient);
  }, [handleDeleteNutrient]);

  // FIX: use memoized values
  const nutrientTableData = useAdvanceTable({
    data: nutrientData,
    columns: nutrientColumns,
    pagination: false,
    sortable: false,
    selection: false,
    initialState: {
      columnVisibility: {}
    }
  });

  const color = ['#ED4D18', '#1ACEDA', '#FEB91A', '#4E7CB6', '#A1A1A1'];
  return (
    <>
      <Modal
        centered
        show={!!selectedEvent && modalName == 'info-modal'}
        onHide={handleClose}
        contentClassName="border"
        size="xl"
        backdrop={modalName === 'add-food-modal'}
      >
        <Modal.Body className="px-card pb-card pt-1 fs-9">
          <Row>
            <Col
              className="m-3"
              style={{
                background: '#e8e8e8',
                padding: '10px',
                borderRadius: '10px'
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <Modal.Title className="modal-title text-body-highlight mb-0">
                  Overview{' '}
                  {selectedEvent &&
                    moment(selectedEvent.extendedProps.createdAt).format(
                      'DD-MM-YYYY hh:mm A'
                    )}
                </Modal.Title>

                <Button className="p-1" variant="link" onClick={handleClose}>
                  <FontAwesomeIcon icon={faTimes} className="fs-8" />
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Lightbox {...lightboxProps} />
            <Col sm={4} md={4} className="card-image">
              <img
                src={selectedEvent?.extendedProps.mealFile}
                alt=""
                width="85%"
                className="ms-3"
                style={{ border: '10px solid #e8e8e8', borderRadius: '20px' }}
                onClick={() => openLightbox(1)}
              />
            </Col>

            <Col sm={8} md={8}>
              {selectedEvent?.extendedProps?.nutrient?.length > 0 ? (
                <FoodPaichart
                  nutrient={selectedEvent?.extendedProps.nutrient}
                />
              ) : (
                <div className="text-start ms-10 mt-4 text-muted">
                  No item found. Please add.
                </div>
              )}
            </Col>
          </Row>
          <Row>
            <Col
              className="m-3"
              style={{
                background: '#e8e8e8',
                padding: '10px',
                borderRadius: '10px'
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <Modal.Title
                  className="modal-title text-body-highlight mb-0"
                  style={{ display: 'inline' }}
                >
                  Nutrition details
                </Modal.Title>
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm={12} md={12} className="mt-3">
              {selectedEvent?.extendedProps?.nutrient?.length > 0 ? (
                <AdvanceTableProvider {...nutrientTableData}>
                  <NutritionTable />
                </AdvanceTableProvider>
              ) : (
                <div className="text-center mt-3 text-muted">
                  No food items available.
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2 px-card pt-0 border-top-0">
          <Button
            onClick={() => handleEdit()}
            variant="phoenix-secondary"
            size="sm"
            startIcon={<FontAwesomeIcon icon={faPencilAlt} className="fs-10" />}
            title="Edit meal details"
          >
            Edit
          </Button>
          <Button
            onClick={() => handleRemove()}
            variant="phoenix-danger"
            size="sm"
            startIcon={<FontAwesomeIcon icon={faTrash} className="fs-9" />}
            title="Delete this meal"
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={modalName == 'edit-info-modal'}
        onHide={handleCloseEditInfoModal}
        contentClassName="border"
        size="lg"
        backdrop={modalName === 'add-food-modal'}
      >
        <Modal.Header className="ps-card border-bottom border-translucent">
          <div>
            <Modal.Title className="modal-title text-body-highlight mb-0">
              {selectedEvent?.title}
            </Modal.Title>
            {selectedEvent?.extendedProps.organizer && (
              <p className="mb-0 fs-9 mt-1">
                by <Link to="#!">{selectedEvent.extendedProps.organizer}</Link>
              </p>
            )}
          </div>
          <Button className="p-1" onClick={handleCloseEditInfoModal}>
            <FontAwesomeIcon icon={faTimes} className="fs-8" />
          </Button>
        </Modal.Header>
        <Modal.Body className="px-card pb-card pt-1 fs-9">
          <div>
            <div
              className={classNames('mt-4', {
                'border-bottom border-translucent pb-3':
                  selectedEvent?.extendedProps.location
              })}
            >
              <div className="mb-1 mt-2">
                {events?.extendedProps.nutrient ? (
                  <>
                    {events?.extendedProps.nutrient.map(
                      (nutrient: any, i: number) => (
                        <Row className="mb-3" key={i}>
                          <Col>
                            <h5> {nutrient.name} </h5>
                          </Col>
                          <Col className="d-flex">
                            <input
                              type="number"
                              className="form-control"
                              value={nutrient.quantity}
                              onChange={e =>
                                handleNutrientChange(i, e.target.value)
                              }
                              style={{ width: '80%' }}
                            />
                            {/* display unit in same line */}
                            <span
                              className="fs-9 text-body-secondary"
                              style={{
                                marginLeft: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              gm
                            </span>
                          </Col>
                        </Row>
                      )
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end px-card pt-0 border-top-0">
          <Button
            onClick={() => handleOpenFoodModal()}
            size="sm"
            variant="phoenix-secondary"
            startIcon={<FontAwesomeIcon icon={faPlus} className="fs-10" />}
            className="ms-2"
            title="Add a food item from the USDA database"
          >
            Add Food
          </Button>
          <Button
            onClick={() => updateMeal()}
            variant="phoenix-secondary"
            size="sm"
            startIcon={<FontAwesomeIcon icon={faPencilAlt} className="fs-10" />}
          >
            Save
          </Button>
          <Button
            onClick={handleCloseEditInfoModal}
            variant="phoenix-danger"
            size="sm"
            startIcon={<FontAwesomeIcon icon={faTrash} className="fs-9" />}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Food Item Modal */}
      <AddFoodItemModal
        show={modalName === 'add-food-modal'}
        onClose={() => handleCloseFoodModal()}
        onAddItem={handleAddFoodItem}
        mealId={selectedEvent?.id || ''}
      />

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Delete Food Item</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Are you sure you want to delete this food item?
          <br />
          This action cannot be undone.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            onClick={confirmDeleteNutrient}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notification */}
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ zIndex: 9999 }}
      >
        {toastMessage && (
          <Toast
            onClose={() => setToastMessage(null)}
            show={!!toastMessage}
            delay={3000}
            autohide
            bg={toastMessage.type === 'success' ? 'success' : 'danger'}
          >
            <Toast.Body className="text-white">
              {toastMessage.message}
            </Toast.Body>
          </Toast>
        )}
      </ToastContainer>
    </>
  );
};

export default CalendarEventModal;
